// Schema describing each editable section in the admin panel.
// Field types: text, textarea, image, icon, list (with item_fields).

export const CONTENT_SCHEMA = [
  {
    key: "company",
    label: "Company Info",
    icon: "Building2",
    description: "Brand name, tagline, contact details and footer copy.",
    fields: {
      name: { type: "text", label: "Company Name" },
      tagline: { type: "text", label: "Tagline" },
      short: { type: "textarea", label: "Short Description (used on Home + Footer)" },
      email: { type: "text", label: "Email" },
      phone: { type: "text", label: "Phone" },
      address: { type: "text", label: "Address" },
      hours: { type: "text", label: "Trading Hours" },
      logo_text_1: { type: "text", label: "Logo Line 1 (e.g. VISSERA)" },
      logo_text_2: { type: "text", label: "Logo Line 2 (e.g. PRECIOUS METALS)" },
    },
  },
  {
    key: "hero",
    label: "Hero Section",
    icon: "Sparkles",
    description: "The big headline at the top of the homepage.",
    fields: {
      eyebrow: { type: "text", label: "Eyebrow (small label above headline)" },
      headline_part1: { type: "text", label: "Headline — first part" },
      headline_gold1: { type: "text", label: "Headline — first gold word" },
      headline_join: { type: "text", label: "Headline — joiner (and)" },
      headline_gold2: { type: "text", label: "Headline — second gold word" },
      subheadline: { type: "textarea", label: "Subheadline / description" },
      cta_primary: { type: "text", label: "Primary CTA Text" },
      cta_secondary: { type: "text", label: "Secondary CTA Text" },
      badge_gold_label: { type: "text", label: "Gold Badge Label" },
      badge_gold_value: { type: "text", label: "Gold Badge Value" },
      badge_silver_label: { type: "text", label: "Silver Badge Label" },
      badge_silver_value: { type: "text", label: "Silver Badge Value" },
    },
  },
  {
    key: "intro",
    label: "Intro Block",
    icon: "Layout",
    description: "The 'At the Heart of the Global Metals Trade' section.",
    fields: {
      eyebrow: { type: "text", label: "Eyebrow" },
      heading: { type: "textarea", label: "Heading (use \\n for line break)" },
      paragraph1: { type: "textarea", label: "Paragraph 1" },
      paragraph2: { type: "textarea", label: "Paragraph 2" },
      badge_label: { type: "text", label: "Image Badge Label" },
      badge_text: { type: "text", label: "Image Badge Text" },
      cta: { type: "text", label: "CTA Button Text" },
    },
  },
  {
    key: "ticker",
    label: "Price Ticker",
    icon: "TrendingUp",
    description: "Labels shown above the live gold/silver price ticker on the homepage.",
    fields: {
      heading: { type: "text", label: "Heading" },
      updating: { type: "text", label: "Loading label (e.g. Updating...)" },
      updated_prefix: { type: "text", label: "Updated prefix" },
      utc_suffix: { type: "text", label: "UTC suffix" },
    },
  },
  {
    key: "home_copy",
    label: "Home Page Copy",
    icon: "Newspaper",
    description: "Section headings and CTA copy on the homepage.",
    fields: {
      services_eyebrow: { type: "text", label: "Services eyebrow" },
      services_title_part1: { type: "text", label: "Services title — part 1" },
      services_title_highlight: { type: "text", label: "Services title — highlight" },
      services_title_part2: { type: "text", label: "Services title — part 2" },
      services_cta: { type: "text", label: "Services CTA label" },
      products_eyebrow: { type: "text", label: "Products eyebrow" },
      products_title_part1: { type: "text", label: "Products title — part 1" },
      products_title_highlight: { type: "text", label: "Products title — highlight" },
      products_title_part2: { type: "text", label: "Products title — part 2" },
      products_cta: { type: "text", label: "Products CTA label" },
      bottom_cta_eyebrow: { type: "text", label: "Bottom CTA eyebrow" },
      bottom_cta_title: { type: "textarea", label: "Bottom CTA title" },
      bottom_cta_body: { type: "textarea", label: "Bottom CTA body" },
      bottom_cta_button: { type: "text", label: "Bottom CTA button label" },
    },
  },
  {
    key: "services_page",
    label: "Services Page Copy",
    icon: "Briefcase",
    description: "Headline, process heading, and CTA copy on the Services page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_part1: { type: "text", label: "Header title — part 1" },
      header_title_highlight: { type: "text", label: "Header title — highlight" },
      header_title_part2: { type: "text", label: "Header title — part 2" },
      header_intro: { type: "textarea", label: "Header intro" },
      process_eyebrow: { type: "text", label: "Process eyebrow" },
      process_title_part1: { type: "text", label: "Process title — part 1" },
      process_title_highlight: { type: "text", label: "Process title — highlight" },
      process_title_part2: { type: "text", label: "Process title — part 2" },
      cta_title: { type: "textarea", label: "CTA title" },
      cta_button: { type: "text", label: "CTA button label" },
    },
  },
  {
    key: "products_page",
    label: "Products Page Copy",
    icon: "Package",
    description: "Headline, specs heading, and CTA copy on the Products page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_part1: { type: "text", label: "Header title — part 1" },
      header_title_highlight: { type: "text", label: "Header title — highlight" },
      header_title_part2: { type: "text", label: "Header title — part 2" },
      header_intro: { type: "textarea", label: "Header intro" },
      specs_eyebrow: { type: "text", label: "Specs eyebrow" },
      specs_title: { type: "text", label: "Specs title" },
      custom_eyebrow: { type: "text", label: "Custom order eyebrow" },
      custom_title: { type: "textarea", label: "Custom order title" },
      custom_button: { type: "text", label: "Custom order button label" },
    },
  },
  {
    key: "products_specs",
    label: "Products Specs List",
    icon: "ListOrdered",
    type: "list",
    description: "The 'What we supply' spec list on the Products page.",
    item_fields: {
      label: { type: "text", label: "Label" },
      value: { type: "textarea", label: "Value" },
    },
  },
  {
    key: "about_page",
    label: "About Page Copy",
    icon: "Users",
    description: "Headline and section copy on the About page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_prefix: { type: "text", label: "Header title — prefix" },
      header_title_highlight_fallback: { type: "text", label: "Header title — highlight fallback" },
      header_intro: { type: "textarea", label: "Header intro" },
      story_eyebrow: { type: "text", label: "Story eyebrow" },
      story_title: { type: "text", label: "Story title" },
      story_p1_fallback: { type: "textarea", label: "Story paragraph 1 (fallback)" },
      story_p2: { type: "textarea", label: "Story paragraph 2" },
      values_eyebrow: { type: "text", label: "Values eyebrow" },
      values_title: { type: "textarea", label: "Values title" },
      markets_eyebrow: { type: "text", label: "Markets eyebrow" },
      markets_title: { type: "textarea", label: "Markets title" },
      markets_body: { type: "textarea", label: "Markets body" },
      cta_eyebrow: { type: "text", label: "CTA eyebrow" },
      cta_title: { type: "textarea", label: "CTA title" },
      cta_button: { type: "text", label: "CTA button label" },
    },
  },
  {
    key: "about_mission_vision",
    label: "About Mission/Vision",
    icon: "Heart",
    type: "list",
    description: "Mission and vision blocks on the About page.",
    item_fields: {
      label: { type: "text", label: "Label (MISSION/VISION)" },
      title: { type: "textarea", label: "Title" },
      body: { type: "textarea", label: "Body" },
    },
  },
  {
    key: "contact_page",
    label: "Contact Page Copy",
    icon: "Mail",
    description: "Headline, form messages, and FAQ header copy on the Contact page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_part1: { type: "text", label: "Header title — part 1" },
      header_title_highlight: { type: "text", label: "Header title — highlight" },
      header_title_part2: { type: "text", label: "Header title — part 2" },
      header_intro: { type: "textarea", label: "Header intro" },
      form_title: { type: "text", label: "Form title" },
      form_required_note: { type: "text", label: "Required note" },
      success_title: { type: "text", label: "Success title" },
      success_body: { type: "textarea", label: "Success body" },
      success_button: { type: "text", label: "Success button label" },
      privacy_note: { type: "textarea", label: "Privacy note" },
      submit_label: { type: "text", label: "Submit label" },
      submitting_label: { type: "text", label: "Submitting label" },
      contact_card_eyebrow: { type: "text", label: "Contact card eyebrow" },
      contact_card_title: { type: "text", label: "Contact card title" },
      response_eyebrow: { type: "text", label: "Response eyebrow" },
      response_body: { type: "textarea", label: "Response body" },
      faqs_eyebrow: { type: "text", label: "FAQs eyebrow" },
      faqs_title_part1: { type: "text", label: "FAQs title — part 1" },
      faqs_title_highlight: { type: "text", label: "FAQs title — highlight" },
      faqs_title_part2: { type: "text", label: "FAQs title — part 2" },
    },
  },
  {
    key: "compliance_page",
    label: "Compliance Page Copy",
    icon: "Scale",
    description: "Headline and note section copy on the Compliance page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_part1: { type: "text", label: "Header title — part 1" },
      header_title_highlight: { type: "text", label: "Header title — highlight" },
      header_title_part2: { type: "text", label: "Header title — part 2" },
      header_intro: { type: "textarea", label: "Header intro" },
      note_eyebrow: { type: "text", label: "Note eyebrow" },
      note_body: { type: "textarea", label: "Note body" },
    },
  },
  {
    key: "quality_page",
    label: "Quality Page Copy",
    icon: "Award",
    description: "Headline copy on the Quality page.",
    fields: {
      header_eyebrow: { type: "text", label: "Header eyebrow" },
      header_title_part1: { type: "text", label: "Header title — part 1" },
      header_title_highlight: { type: "text", label: "Header title — highlight" },
      header_title_part2: { type: "text", label: "Header title — part 2" },
      header_intro: { type: "textarea", label: "Header intro" },
    },
  },
  {
    key: "trust_strip",
    label: "Trust Strip",
    icon: "ShieldCheck",
    type: "list",
    description: "The 4-icon strip just below the hero.",
    item_fields: {
      icon: { type: "icon", label: "Icon (lucide name)" },
      title: { type: "text", label: "Title" },
      text: { type: "text", label: "Description" },
    },
  },
  {
    key: "stats",
    label: "Stats Numbers",
    icon: "TrendingUp",
    type: "list",
    description: "The big numbers band (years, countries, partners, etc.).",
    item_fields: {
      value: { type: "text", label: "Value (e.g. 12+)" },
      label: { type: "text", label: "Label" },
    },
  },
  {
    key: "services",
    label: "Services",
    icon: "Briefcase",
    type: "list",
    description: "The service cards on the Services page and Home preview.",
    item_fields: {
      key: { type: "text", label: "Key (slug)" },
      title: { type: "text", label: "Title" },
      short: { type: "textarea", label: "Description" },
      icon: { type: "icon", label: "Icon (lucide name)" },
    },
  },
  {
    key: "products",
    label: "Products",
    icon: "Package",
    type: "list",
    description: "Product cards. The 'image' field is an image key from the Images tab OR a full URL.",
    item_fields: {
      title: { type: "text", label: "Title" },
      desc: { type: "text", label: "Description" },
      image: { type: "image", label: "Image (URL or upload)" },
      specs: { type: "stringlist", label: "Specifications (one per line)" },
    },
  },
  {
    key: "values",
    label: "Values",
    icon: "Heart",
    type: "list",
    description: "Company values shown on the About page.",
    item_fields: {
      title: { type: "text", label: "Title" },
      text: { type: "text", label: "Description" },
    },
  },
  {
    key: "markets",
    label: "Markets We Serve",
    icon: "Users",
    type: "list",
    description: "Client segments listed on the About page.",
    item_fields: {
      title: { type: "text", label: "Title" },
      text: { type: "text", label: "Description" },
    },
  },
  {
    key: "about_stats",
    label: "About Stats",
    icon: "TrendingUp",
    type: "list",
    description: "The big numbers band shown on the About page.",
    item_fields: {
      value: { type: "text", label: "Value (e.g. 12+)" },
      label: { type: "text", label: "Label" },
    },
  },
  {
    key: "process",
    label: "Process Steps",
    icon: "ListOrdered",
    type: "list",
    description: "The 'How it works' steps on the Services page.",
    item_fields: {
      n: { type: "text", label: "Number (e.g. 01)" },
      title: { type: "text", label: "Title" },
      text: { type: "text", label: "Description" },
    },
  },
  {
    key: "faqs",
    label: "FAQs",
    icon: "HelpCircle",
    type: "list",
    description: "Frequently asked questions on the Contact page.",
    item_fields: {
      q: { type: "text", label: "Question" },
      a: { type: "textarea", label: "Answer" },
    },
  },
  {
    key: "quality_blocks",
    label: "Quality Blocks",
    icon: "Award",
    type: "list",
    description: "The blocks on the Quality page.",
    item_fields: {
      icon: { type: "icon", label: "Icon" },
      title: { type: "text", label: "Title" },
      text: { type: "textarea", label: "Description" },
    },
  },
  {
    key: "compliance_blocks",
    label: "Compliance Blocks",
    icon: "Scale",
    type: "list",
    description: "The blocks on the Compliance page.",
    item_fields: {
      icon: { type: "icon", label: "Icon" },
      title: { type: "text", label: "Title" },
      text: { type: "textarea", label: "Description" },
    },
  },
];

// Image library — slots used across the site. The Images tab lets the admin set each.
export const IMAGE_SLOTS = [
  { key: "heroGold", label: "Hero — Gold/Silver Bars", aspect: "5/4" },
  { key: "istanbul", label: "Home — Istanbul Skyline Card", aspect: "5/4" },
  { key: "istanbulBridge", label: "About — Istanbul (vertical)", aspect: "4/5" },
  { key: "goldBars", label: "Product — Gold Bars", aspect: "4/3" },
  { key: "silverBars", label: "Product — Silver Bars", aspect: "4/3" },
  { key: "coins", label: "Product — Coins", aspect: "4/3" },
  { key: "granules", label: "Product — Granules", aspect: "4/3" },
  { key: "refinery", label: "Quality — Refinery Image", aspect: "4/5" },
  { key: "vault", label: "Compliance — Vault Image", aspect: "4/5" },
  { key: "logistics", label: "Logistics — (extra)", aspect: "16/9" },
  { key: "team", label: "Team — (extra)", aspect: "16/9" },
];
